import type { TextAreaFieldProps } from '@iheartjane/reefer';
import { TextAreaField } from '@iheartjane/reefer';
import { forwardRef } from 'react';

import type { FormFieldProps } from '../formField.types';
import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export interface FormTextAreaFieldProps
  extends
    Omit<TextAreaFieldProps, 'children'>,
    Omit<FormFieldProps, 'max' | 'min'> {}

/**
 * TextArea fields define multi-line text input controls.
 *
 * _Field validations supported:_ `required`, `minLength`, `maxLength`, `pattern`, `validate`.
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE FORMS**
 *
 * _For a similar component for use outside forms, see [`TextAreaField`](/story/components-forms-fields-textareafield--default)._
 */
export const FormTextAreaField = forwardRef<
  HTMLTextAreaElement,
  FormTextAreaFieldProps
>(function FormTextAreaField(
  {
    autoFocus = false,
    defaultValue = '',
    disabled = false,
    autocomplete,
    helperText,
    errorMessage,
    errorOnBlur = false,
    label,
    labelHidden = false,
    maxLength,
    minLength,
    name,
    onBlur,
    onChange,
    pattern,
    placeholder,
    readOnly = false,
    required = false,
    resize = true,
    rows,
    shouldUnregister,
    'data-testid': testId,
    validate,
    width = 'auto',
    ...props
  }: FormTextAreaFieldProps,
  forwardedRef
) {
  return (
    <FormFieldWrapper
      defaultValue={defaultValue}
      errorMessage={errorMessage}
      errorOnBlur={errorOnBlur}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      pattern={pattern}
      render={({ children, field, invalid }) => (
        <TextAreaField
          aria-invalid={invalid}
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          disabled={disabled}
          autocomplete={autocomplete}
          helperText={helperText}
          label={label}
          labelHidden={labelHidden}
          name={field.name}
          onBlur={(value) => {
            onBlur && onBlur(value);
            field.onBlur();
          }}
          onChange={(value) => {
            onChange && onChange(value);
            field.onChange(value);
          }}
          onUpdate={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={(el) => {
            field.ref(el);
            if (forwardedRef) {
              if (typeof forwardedRef === 'function') {
                forwardedRef(el);
              } else {
                forwardedRef.current = el;
              }
            }
          }}
          required={required}
          resize={resize}
          rows={rows}
          data-testid={testId}
          value={field.value}
          width={width}
          {...props}
        >
          {children}
        </TextAreaField>
      )}
      required={required}
      shouldUnregister={shouldUnregister}
      validate={validate}
    />
  );
});
